/**
 * Theme bridge to dz-shared-styles. Values are CSS custom property
 * references — the widget root sets `color-data` and inherits the rest from
 * the host page (or src/styles/dev-fallback.scss in standalone dev).
 *
 * Keep using `theme.colors.primary` etc. throughout the codebase; each
 * styled-component string interpolates a `var(--…)` ref and the browser
 * resolves it at render time.
 *
 * NOTE: chart libraries (recharts) accept these as `fill`/`stroke` SVG
 * attributes, where modern browsers resolve var() correctly. For places
 * that need raw hex (color math, gradient stop interpolation), use the
 * literal constants in `utils/colors` instead.
 */

/**
 * Design-space px -> a length in the widget's own type scale.
 *
 * `rem` cannot do this job: it always resolves against the document root, and
 * the host page (dezernatzukunft.org) sets `html { font-size: 13px }`, so every
 * rem in the widget rendered at 81% of its design size — 14px text came out at
 * 11.4px. The widget root publishes `--dz-font-base` instead (see Container in
 * App.tsx), so exactly one declaration decides the base and the numbers at the
 * call sites stay in design space: `scaled(14)` is the 14px from the design.
 *
 * The 16px fallback is not decoration: the modal and the dialogs portal to
 * document.body, outside the widget root, where the variable does not resolve.
 */
export const scaled = (px: number) => `calc(${+(px / 16).toFixed(5)} * var(--dz-font-base, 16px))`;

export const theme = {
  colors: {
    primary: 'var(--primary-color)',
    secondary: 'var(--color-text-default)',
    background: 'var(--color-bg-surface)',
    // Cards/surfaces sit on the same beige page bg per shared design — visual
    // separation comes from --shadow-s on `.tile`, not a different fill.
    surface: 'var(--color-bg-surface)',
    text: {
      primary: 'var(--color-text-default)',
      secondary: 'var(--color-text-muted)',
      muted: 'var(--color-text-muted)',
    },
    border: 'var(--color-bg-muted)',
    error: '#F44336',
    success: '#2E7D32',
    warning: '#F57C00',
  },
  fontFamily: 'var(--serif-stack)',
  fontFamilySans: 'var(--sans-stack)',
  // Design px, converted through the widget's own base (see `scaled` above).
  fontSize: {
    xs: scaled(12),
    sm: scaled(13),
    md: scaled(16),
    lg: scaled(20),
    xl: scaled(30),
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  borderRadius: {
    sm: 'var(--radius-s)',
    md: 'var(--radius-m)',
    lg: 'var(--radius-l)',
    xl: 'var(--radius-l)',
  },
  shadow: {
    sm: 'var(--shadow-s)',
    md: 'var(--shadow-m)',
    lg: 'var(--shadow-l)',
  },
  spacing: {
    // Mapped to dz-shared-styles space() scale (3px base):
    //   xs ≈ 3px, sm ≈ 9px, md ≈ 15px, lg ≈ 24px, xl ≈ 30px
    xs: 'var(--space-1)',
    sm: 'var(--space-3)',
    md: 'var(--space-5)',
    lg: 'var(--space-8)',
    xl: 'var(--space-10)',
  },
} as const;

export type Theme = typeof theme;
